/* Locally Twisted — shared accessible custom listbox (lt-select)
   Replaces the native <select> popup for any <select data-lt-listbox>
   found in the document — the OS-rendered popup can't be controlled by
   CSS (contact-method cut off; occasion too wide + scrollbar on mobile;
   later reused for product-page text-only option controls). The native
   <select> stays in the DOM as the submitting control and no-JS fallback;
   this UI writes back to it. WAI-ARIA button + listbox pattern with
   aria-activedescendant.
   Extracted 2026-07-12 from theme/sections/lt-contact.liquid (unchanged
   logic) so theme/snippets/lt-product-extras.liquid can reuse it. */
(function () {
  'use strict';
  var uid = 0;

  function enhance(select) {
    if (!select || select.disabled || select.dataset.ltListboxReady) return;

    var id = 'lt-ls-' + (uid++);
    var label = document.querySelector('label[for="' + select.id + '"]');
    if (label && !label.id) label.id = id + '-label';
    var labelId = label ? label.id : null;

    var options = Array.prototype.slice.call(select.options);

    var wrap = document.createElement('div');
    wrap.className = 'lt-select';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'lt-select__button';
    button.id = id + '-button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', id + '-popup');
    if (labelId) button.setAttribute('aria-labelledby', labelId + ' ' + id + '-button-label');

    var labelSpan = document.createElement('span');
    labelSpan.className = 'lt-select__button-label';
    labelSpan.id = id + '-button-label';
    button.appendChild(labelSpan);

    var popup = document.createElement('ul');
    popup.className = 'lt-select__popup';
    popup.id = id + '-popup';
    popup.setAttribute('role', 'listbox');
    popup.setAttribute('tabindex', '-1');
    popup.hidden = true;
    if (labelId) popup.setAttribute('aria-labelledby', labelId);

    var optionEls = [];
    options.forEach(function (opt, i) {
      var li = document.createElement('li');
      li.className = 'lt-select__option';
      li.id = id + '-opt-' + i;
      li.setAttribute('role', 'option');
      li.dataset.value = opt.value;
      li.dataset.index = String(i);
      li.textContent = opt.textContent;
      li.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
      popup.appendChild(li);
      optionEls.push(li);
    });

    // Build the visual control, then hide the native select (only after success).
    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);
    wrap.appendChild(button);
    wrap.appendChild(popup);
    select.classList.add('lt-select__native');
    select.setAttribute('aria-hidden', 'true');
    select.setAttribute('tabindex', '-1');
    select.dataset.ltListboxReady = 'true';

    // Transfer `required` off the now-hidden native select (a hidden required
    // control makes the browser try to validate/focus an invisible element).
    // Required state remains visible in the source label and is enforced on
    // submit; `aria-required` is not valid on this button/listbox trigger.
    var isRequired = select.required;
    var errorEl = null;
    if (isRequired) {
      // Record that this control is required BEFORE the attribute is removed.
      // Once `required` is gone the flag is unrecoverable, and the submit guard
      // needs it to fail closed if enhancement does not finish (see below).
      select.dataset.ltRequiredTransferred = 'true';
      select.required = false;
      errorEl = document.createElement('p');
      errorEl.className = 'lt-select__error';
      errorEl.id = id + '-error';
      errorEl.textContent = 'Please choose an option.';
      wrap.appendChild(errorEl);
    }
    function clearInvalid() {
      wrap.classList.remove('lt-select--invalid');
      button.removeAttribute('aria-invalid');
      if (errorEl) button.setAttribute('aria-describedby', '');
    }
    wrap.ltValidate = function () {
      if (isRequired && select.value === '') {
        wrap.classList.add('lt-select--invalid');
        button.setAttribute('aria-invalid', 'true');
        if (errorEl) button.setAttribute('aria-describedby', errorEl.id);
        return false;
      }
      clearInvalid();
      return true;
    };

    var activeIndex = Math.max(select.selectedIndex, 0);

    function syncButtonLabel() {
      var opt = select.options[select.selectedIndex] || select.options[0];
      labelSpan.textContent = opt ? opt.textContent : '';
      button.setAttribute('data-placeholder', (opt && opt.value === '') ? 'true' : 'false');
    }

    function setActive(index) {
      if (index < 0) index = 0;
      if (index > optionEls.length - 1) index = optionEls.length - 1;
      optionEls.forEach(function (el) { el.classList.remove('is-active'); });
      activeIndex = index;
      var el = optionEls[activeIndex];
      el.classList.add('is-active');
      popup.setAttribute('aria-activedescendant', el.id);
      // Keep the active option in view within the scroll area.
      var top = el.offsetTop;
      var bottom = top + el.offsetHeight;
      if (top < popup.scrollTop) popup.scrollTop = top;
      else if (bottom > popup.scrollTop + popup.clientHeight) popup.scrollTop = bottom - popup.clientHeight;
    }

    function open() {
      if (!popup.hidden) return;
      popup.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      // Decide flip direction so it is never cut off near the viewport bottom.
      popup.removeAttribute('data-flip');
      var rect = button.getBoundingClientRect();
      var spaceBelow = window.innerHeight - rect.bottom;
      var spaceAbove = rect.top;
      if (popup.offsetHeight > spaceBelow && spaceAbove > spaceBelow) {
        popup.setAttribute('data-flip', 'up');
      }
      setActive(select.selectedIndex >= 0 ? select.selectedIndex : 0);
      popup.focus();
    }

    function close(focusButton) {
      if (popup.hidden) return;
      popup.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      popup.removeAttribute('aria-activedescendant');
      if (focusButton) button.focus();
    }

    function commit(index) {
      select.selectedIndex = index;
      optionEls.forEach(function (el, i) {
        el.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      syncButtonLabel();
      if (isRequired && select.value !== '') clearInvalid();
      select.dispatchEvent(new Event('change', { bubbles: true }));
      close(true);
    }

    button.addEventListener('click', function () {
      if (popup.hidden) open(); else close(true);
    });
    button.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open();
      }
    });

    popup.addEventListener('click', function (e) {
      var li = e.target.closest('.lt-select__option');
      if (li) commit(parseInt(li.dataset.index, 10));
    });
    popup.addEventListener('mousemove', function (e) {
      var li = e.target.closest('.lt-select__option');
      if (li) setActive(parseInt(li.dataset.index, 10));
    });
    popup.addEventListener('keydown', function (e) {
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); setActive(activeIndex + 1); break;
        case 'ArrowUp': e.preventDefault(); setActive(activeIndex - 1); break;
        case 'Home': e.preventDefault(); setActive(0); break;
        case 'End': e.preventDefault(); setActive(optionEls.length - 1); break;
        case 'Enter':
        case ' ': e.preventDefault(); commit(activeIndex); break;
        case 'Escape': e.preventDefault(); close(true); break;
        case 'Tab': close(false); break;
        default: break;
      }
    });

    // Close on outside interaction.
    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target)) close(false);
    });
    wrap.addEventListener('focusout', function () {
      // Defer so focus can land on the popup/button before we test.
      setTimeout(function () {
        if (!wrap.contains(document.activeElement)) close(false);
      }, 0);
    });

    // Keep UI in sync if the value is changed programmatically elsewhere.
    select.addEventListener('change', function () {
      optionEls.forEach(function (el, i) {
        el.setAttribute('aria-selected', i === select.selectedIndex ? 'true' : 'false');
      });
      syncButtonLabel();
    });

    syncButtonLabel();
  }

  // The submitting control is the native select, which may sit outside its
  // form and be associated only by the `form` attribute (product pages do
  // exactly that), so resolve the owner rather than assuming containment.
  function owningForm(select) {
    if (select.form) return select.form;
    var formId = select.getAttribute ? select.getAttribute('form') : null;
    return formId ? document.getElementById(formId) : null;
  }

  function bindSubmitGuard(form) {
    if (!form || form.dataset.ltSelectSubmitReady) return;
    form.dataset.ltSelectSubmitReady = 'true';
    form.addEventListener('submit', function (e) {
      // When the Shopify product form has upgraded it owns validation and
      // sets noValidate; enforcing here too would show the same refusal
      // twice. Without that handshake this guard is the only enforcement
      // left, because `required` has already been transferred off.
      if (form.dataset.ltProductFormValidationReady === 'true') return;
      var wraps = document.querySelectorAll('.lt-select');
      var firstInvalid = null;
      for (var k = 0; k < wraps.length; k++) {
        var native = wraps[k].querySelector('select[data-lt-listbox]');
        if (!native || owningForm(native) !== form) continue;
        if (typeof wraps[k].ltValidate === 'function') {
          if (!wraps[k].ltValidate() && !firstInvalid) firstInvalid = wraps[k];
          continue;
        }
        // No validator on a wrap whose native `required` was already
        // transferred away means `enhance` threw part-way: the control is
        // required, nothing can read its state, and the browser will no longer
        // check it. Refuse the submit rather than let the choice through.
        if (native.dataset.ltRequiredTransferred === 'true' && !firstInvalid) {
          firstInvalid = wraps[k];
        }
      }
      if (firstInvalid) {
        e.preventDefault();
        var btn = firstInvalid.querySelector('.lt-select__button');
        if (btn) btn.focus();
      }
    });
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var selects = root.querySelectorAll('select[data-lt-listbox]');
    for (var i = 0; i < selects.length; i++) {
      try { enhance(selects[i]); } catch (err) { /* leave native select intact */ }
    }

    // Enforce required-ness of enhanced selects on submit (they no longer
    // carry the native `required`, so the browser won't validate them).
    // Bound once per owning form: the contact form and every specialty
    // product form that contains an enhanced select.
    for (var j = 0; j < selects.length; j++) {
      if (!selects[j].dataset.ltListboxReady) continue;
      bindSubmitGuard(owningForm(selects[j]));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
